import { useMutation, useQuery } from '@tanstack/react-query';
import { Download, Upload, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { apiRequest, downloadApiFile, getApiErrorMessage } from '../../lib/api';
import type {
  ManualSurveyAssignment,
  ManualSurveyImportPreview,
  SurveyQuestionDefinition,
  SurveyVisit,
} from '../../types/domain';

export function QuestionInput({
  question,
  value,
  onChange,
}: {
  question: SurveyQuestionDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  if (question.kind === 'yes_no') {
    return <select onChange={(event) => onChange(event.target.value === '' ? undefined : event.target.value === 'yes')} required={question.required} value={value === true ? 'yes' : value === false ? 'no' : ''}><option value="">Select</option><option value="yes">Yes</option><option value="no">No</option></select>;
  }
  if (question.kind === 'single_select') {
    return <select onChange={(event) => onChange(event.target.value || undefined)} required={question.required} value={typeof value === 'string' ? value : ''}><option value="">Select</option>{question.choices.map((choice) => <option key={choice.value} value={choice.value}>{choice.label}</option>)}</select>;
  }
  if (question.kind === 'multi_select') {
    const selected = Array.isArray(value) ? value as string[] : [];
    return <div className="manual-survey-choices">{question.choices.map((choice) => <label key={choice.value}><input checked={selected.includes(choice.value)} onChange={(event) => onChange(event.target.checked ? [...selected, choice.value] : selected.filter((item) => item !== choice.value))} type="checkbox" /> {choice.label}</label>)}</div>;
  }
  if (question.kind === 'long_text') {
    return <textarea onChange={(event) => onChange(event.target.value)} required={question.required} rows={3} value={typeof value === 'string' ? value : ''} />;
  }
  return <input inputMode={question.kind === 'number' ? 'decimal' : undefined} onChange={(event) => onChange(event.target.value)} required={question.required} type={question.kind === 'number' ? 'number' : 'text'} value={typeof value === 'string' ? value : ''} />;
}

function csvAnswerLabel(key: string) {
  return key.replace('answer__', '').replaceAll('_', ' ');
}

export function ManualSurveyDialog({
  accessToken,
  workspaceId,
  onClose,
  onSaved,
}: {
  accessToken: string;
  workspaceId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  const [assignmentId, setAssignmentId] = useState('');
  const [outletMode, setOutletMode] = useState<'new' | 'existing_catalogue'>('new');
  const [catalogueVenueId, setCatalogueVenueId] = useState('');
  const [capturedAt, setCapturedAt] = useState('');
  const [reason, setReason] = useState('');
  const [outlet, setOutlet] = useState({
    name: '', city: '', area: '', address: '', ownerName: '', ownerContact: '',
  });
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [campaignSurveyId, setCampaignSurveyId] = useState('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ManualSurveyImportPreview | null>(null);
  const [selectedCsvRows, setSelectedCsvRows] = useState<Set<number>>(new Set());

  const assignments = useQuery({
    queryKey: ['manual-survey-assignments', workspaceId, accessToken],
    queryFn: () => apiRequest<ManualSurveyAssignment[]>(
      '/surveys/manual-entry/?' + new URLSearchParams({ workspace: workspaceId }).toString(),
      { accessToken },
    ),
  });
  const selectedAssignment = assignments.data?.find((item) => item.id === assignmentId);
  const questions = selectedAssignment?.questions.filter((question) => !['photo', 'location'].includes(question.kind)) ?? [];
  const visibleQuestions = questions.filter((question) => {
    const condition = question.visibility_condition as { question?: string; equals?: unknown };
    if (!condition.question) return true;
    const controlling = questions.find((item) => item.key === condition.question);
    return controlling ? answers[controlling.id] === condition.equals : false;
  });
  const campaignSurveys = useMemo(() => {
    const rows = new Map<string, string>();
    for (const assignment of assignments.data ?? []) {
      rows.set(
        assignment.campaign_survey,
        assignment.campaign_name + ' · ' + assignment.template_name + ' v' + assignment.template_version_number,
      );
    }
    return [...rows].map(([id, label]) => ({ id, label }));
  }, [assignments.data]);

  const createEntry = useMutation({
    mutationFn: () => apiRequest<SurveyVisit>(
      '/surveys/manual-entry/?' + new URLSearchParams({ workspace: workspaceId }).toString(),
      {
        accessToken,
        method: 'POST',
        body: JSON.stringify({
          survey_assignment_id: assignmentId,
          outlet_mode: outletMode,
          catalogue_venue_id: outletMode === 'existing_catalogue' ? catalogueVenueId : null,
          outlet_name: outlet.name,
          outlet_city: outlet.city,
          outlet_area: outlet.area,
          outlet_address: outlet.address,
          outlet_owner_name: outlet.ownerName,
          outlet_owner_contact: outlet.ownerContact,
          answers: questions.flatMap((question) => {
            const value = answers[question.id];
            if (value === undefined || value === '' || (Array.isArray(value) && !value.length)) return [];
            return [{ question_id: question.id, value }];
          }),
          captured_at: new Date(capturedAt).toISOString(),
          latitude: null,
          longitude: null,
          horizontal_accuracy_m: null,
          idempotency_key: crypto.randomUUID(),
          manual_entry_reason: reason,
        }),
      },
    ),
    onSuccess: onSaved,
  });

  const importMutation = useMutation({
    mutationFn: (confirm: boolean) => {
      if (!csvFile) throw new Error('Choose the completed CSV file.');
      const form = new FormData();
      form.set('mode', confirm ? 'confirm' : 'preview');
      form.set('campaign_survey_id', campaignSurveyId);
      form.set('manual_entry_reason', reason);
      form.set('file', csvFile);
      if (confirm && preview) {
        form.set('preview_token', preview.preview_token);
        [...selectedCsvRows].sort((left, right) => left - right).forEach((rowNumber) => form.append('selected_rows', String(rowNumber)));
      }
      return apiRequest<ManualSurveyImportPreview | { imported_count: number }>(
        '/surveys/manual-import/?' + new URLSearchParams({ workspace: workspaceId }).toString(),
        { accessToken, method: 'POST', body: form },
      );
    },
    onSuccess: (data, confirm) => {
      if (confirm) onSaved();
      else {
        const nextPreview = data as ManualSurveyImportPreview;
        setPreview(nextPreview);
        setSelectedCsvRows(new Set(nextPreview.rows.filter((row) => row.status === 'valid').map((row) => row.row_number)));
      }
    },
  });

  async function downloadTemplate() {
    await downloadApiFile(
      '/surveys/manual-import/template/?' + new URLSearchParams({
        workspace: workspaceId,
        campaign_survey: campaignSurveyId,
      }).toString(),
      { accessToken },
    );
  }

  const setOutletValue = (key: keyof typeof outlet, value: string) => {
    setOutlet((current) => ({ ...current, [key]: value }));
  };
  const previewInvalidCount = Number(preview?.invalid_count ?? 0);
  const validPreviewRows = preview?.rows.filter((row) => row.status === 'valid').map((row) => row.row_number) ?? [];
  const allValidPreviewRowsSelected = validPreviewRows.length > 0 && validPreviewRows.every((rowNumber) => selectedCsvRows.has(rowNumber));
  const canImportPreview = Boolean(preview && selectedCsvRows.size > 0);
  const toggleCsvRow = (rowNumber: number) => {
    setSelectedCsvRows((current) => {
      const next = new Set(current);
      if (next.has(rowNumber)) next.delete(rowNumber);
      else next.add(rowNumber);
      return next;
    });
  };
  const toggleAllValidCsvRows = () => {
    setSelectedCsvRows(allValidPreviewRowsSelected ? new Set() : new Set(validPreviewRows));
  };

  return <div className="confirmation-backdrop" role="presentation">
    <section aria-label="Manual field survey entry" aria-modal="true" className="confirmation-dialog manual-survey-dialog" role="dialog">
      <button aria-label="Close manual survey entry" className="icon-button confirmation-close" onClick={onClose} type="button"><X size={19} /></button>
      <p className="eyebrow">Paper fallback</p>
      <h2>Upload field surveys manually</h2>
      <p>Records remain labelled as manually entered and do not claim mobile GPS or photo verification.</p>
      <nav aria-label="Manual survey methods" className="campaign-setup-tabs survey-tabs manual-survey-tabs">
        <button className={mode === 'single' ? 'active' : ''} onClick={() => setMode('single')} type="button">Enter one form</button>
        <button className={mode === 'bulk' ? 'active' : ''} onClick={() => setMode('bulk')} type="button">Import CSV</button>
      </nav>
      {assignments.isError ? <p className="form-error">{getApiErrorMessage(assignments.error)}</p> : null}
      {mode === 'single' ? <form className="management-form" onSubmit={(event) => { event.preventDefault(); createEntry.mutate(); }}>
        <div className="form-grid">
          <label>BA survey authorization<select onChange={(event) => { setAssignmentId(event.target.value); setAnswers({}); }} required value={assignmentId}><option value="">Select campaign and BA</option>{(assignments.data ?? []).map((assignment) => <option key={assignment.id} value={assignment.id}>{assignment.campaign_name} · {assignment.brand_ambassador_name} · {assignment.template_name} v{assignment.template_version_number}</option>)}</select></label>
          <label>Paper collection time<input onChange={(event) => setCapturedAt(event.target.value)} required type="datetime-local" value={capturedAt} /></label>
          <label>Shop source<select onChange={(event) => setOutletMode(event.target.value as typeof outletMode)} value={outletMode}><option value="new">New/discovered shop</option><option value="existing_catalogue">Campaign catalogue store</option></select></label>
          {outletMode === 'existing_catalogue' ? <label>Catalogue store<select onChange={(event) => setCatalogueVenueId(event.target.value)} required value={catalogueVenueId}><option value="">Select store</option>{(selectedAssignment?.catalogue_stores ?? []).map((store) => <option key={store.id} value={store.id}>{store.name} · {store.city}</option>)}</select></label> : <>
            <label>Shop name<input onChange={(event) => setOutletValue('name', event.target.value)} required value={outlet.name} /></label>
            <label>City<input onChange={(event) => setOutletValue('city', event.target.value)} required value={outlet.city} /></label>
            <label>Area<input onChange={(event) => setOutletValue('area', event.target.value)} value={outlet.area} /></label>
            <label>Address<input onChange={(event) => setOutletValue('address', event.target.value)} value={outlet.address} /></label>
            <label>Owner name<input onChange={(event) => setOutletValue('ownerName', event.target.value)} value={outlet.ownerName} /></label>
            <label>Owner contact<input onChange={(event) => setOutletValue('ownerContact', event.target.value)} value={outlet.ownerContact} /></label>
          </>}
        </div>
        {visibleQuestions.length ? <section className="manual-survey-questions"><h3>Paper answers</h3>{visibleQuestions.map((question) => <label key={question.id}><span>{question.label}{question.required ? ' *' : ''}</span><QuestionInput onChange={(value) => setAnswers((current) => ({ ...current, [question.id]: value }))} question={question} value={answers[question.id]} />{question.help_text ? <small>{question.help_text}</small> : null}</label>)}</section> : null}
        <label>Reason for manual entry<textarea minLength={10} onChange={(event) => setReason(event.target.value)} required rows={3} value={reason} /></label>
        {createEntry.isError ? <p className="form-error">{getApiErrorMessage(createEntry.error)}</p> : null}
        <div className="confirmation-actions"><button className="button secondary" onClick={onClose} type="button">Cancel</button><button className="button primary" disabled={createEntry.isPending || !assignmentId} type="submit">{createEntry.isPending ? 'Saving…' : 'Save manual survey'}</button></div>
      </form> : <section className="management-form manual-survey-import">
        <label>Survey form<select onChange={(event) => { setCampaignSurveyId(event.target.value); setPreview(null); setSelectedCsvRows(new Set()); }} required value={campaignSurveyId}><option value="">Select campaign survey</option>{campaignSurveys.map((survey) => <option key={survey.id} value={survey.id}>{survey.label}</option>)}</select></label>
        <button className="button secondary" disabled={!campaignSurveyId} onClick={() => void downloadTemplate()} type="button"><Download size={16} /> Download CSV template</button>
        <label>Completed CSV<input accept=".csv,text/csv" onChange={(event) => { setCsvFile(event.target.files?.[0] ?? null); setPreview(null); setSelectedCsvRows(new Set()); }} type="file" /></label>
        <label><span className="required-label">Reason for manual import <span aria-hidden="true" className="required-marker">*</span></span><textarea minLength={1} onChange={(event) => { setReason(event.target.value); setPreview(null); setSelectedCsvRows(new Set()); }} required rows={3} value={reason} /></label>
        {importMutation.isError ? <p className="form-error">{getApiErrorMessage(importMutation.error)}</p> : null}
        {preview ? <section className="manual-import-preview"><h3>Review CSV data</h3><p><strong>{preview.valid_count}</strong> valid · <strong>{preview.invalid_count}</strong> invalid · <strong>{selectedCsvRows.size}</strong> selected</p>{previewInvalidCount > 0 ? <p className="inline-help">Invalid rows cannot be selected and will not be imported.</p> : null}<div className="data-table-wrap"><table aria-label="CSV survey preview" className="data-table manual-import-preview-table"><thead><tr><th><input aria-label="Select all eligible survey rows" checked={allValidPreviewRowsSelected} disabled={validPreviewRows.length === 0} onChange={toggleAllValidCsvRows} type="checkbox" /></th><th>Row</th><th>Status</th><th>Brand ambassador</th><th>Captured</th><th>Shop</th><th>Location</th><th>Owner</th><th>Coordinates</th><th>Answers</th></tr></thead><tbody>{preview.rows.map((row) => {
          const values = row.values ?? {};
          const answerValues = Object.entries(values).filter(([key, value]) => key.startsWith('answer__') && value !== '');
          const coordinates = values.latitude && values.longitude ? `${values.latitude}, ${values.longitude}` : 'Not provided';
          return <tr key={row.row_number}><td><input aria-label={`Select survey row ${row.row_number}`} checked={selectedCsvRows.has(row.row_number)} disabled={row.status !== 'valid'} onChange={() => toggleCsvRow(row.row_number)} type="checkbox" /></td><td>{row.row_number}</td><td><strong className={`manual-import-row-status ${row.status}`}>{row.status}</strong><small>{row.detail}</small></td><td>{values.ba_email || 'Not provided'}</td><td>{values.captured_at || 'Not provided'}</td><td><strong>{values.outlet_name || 'Not provided'}</strong><small>{values.outlet_address}</small></td><td>{[values.outlet_area, values.outlet_city].filter(Boolean).join(' · ') || 'Not provided'}</td><td><strong>{values.outlet_owner_name || 'Not provided'}</strong><small>{values.outlet_owner_contact}</small></td><td>{coordinates}</td><td>{answerValues.length ? <dl className="manual-import-answer-list">{answerValues.map(([key, value]) => <div key={key}><dt>{csvAnswerLabel(key)}</dt><dd>{value}</dd></div>)}</dl> : 'No answers'}</td></tr>;
        })}</tbody></table></div></section> : null}
        <div className="confirmation-actions"><button className="button secondary" onClick={onClose} type="button">Cancel</button>{preview ? <><button className="button secondary" disabled={!campaignSurveyId || !csvFile || !reason.trim() || importMutation.isPending} onClick={() => importMutation.mutate(false)} type="button">{importMutation.isPending ? 'Checking…' : 'Preview again'}</button><button className="button primary" disabled={!canImportPreview || importMutation.isPending} onClick={() => importMutation.mutate(true)} type="button"><Upload size={16} /> {importMutation.isPending ? 'Importing…' : selectedCsvRows.size > 0 ? 'Import ' + selectedCsvRows.size + ' selected surveys' : 'Select valid rows to import'}</button></> : <button className="button primary" disabled={!campaignSurveyId || !csvFile || !reason.trim() || importMutation.isPending} onClick={() => importMutation.mutate(false)} type="button">{importMutation.isPending ? 'Checking…' : 'Preview CSV'}</button>}</div>
      </section>}
    </section>
  </div>;
}
